<script setup>
import { ref, computed, onMounted } from 'vue';
import { useVuelidate } from '@vuelidate/core';
import { required, url } from '@vuelidate/validators';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { useStore } from 'vuex';
import NextButton from 'dashboard/components-next/button/Button.vue';

const props = defineProps({
  show: {
    type: Boolean,
    default: false,
  },
  mode: {
    type: String,
    default: 'CREATE',
  },
  selectedAppData: {
    type: Object,
    default: () => ({}),
  },
});

const emit = defineEmits(['close']);

const { t } = useI18n();
const store = useStore();

const isLoading = ref(false);
const app = ref({
  name: '',
  description: '',
  url: '',
  icon: 'i-lucide-app-window',
  status: 'enabled',
});

const rules = computed(() => ({
  app: {
    name: { required },
    url: { required, url },
  },
}));

const v$ = useVuelidate(rules, { app });

const header = computed(() =>
  t(`INTEGRATION_SETTINGS.IFRAME_APPS.${props.mode}.HEADER`)
);

const submitButtonLabel = computed(() =>
  t(`INTEGRATION_SETTINGS.IFRAME_APPS.${props.mode}.FORM_SUBMIT`)
);

onMounted(() => {
  if (props.mode === 'UPDATE' && props.selectedAppData) {
    app.value.name = props.selectedAppData.name;
    app.value.description = props.selectedAppData.description || '';
    app.value.url = props.selectedAppData.url;
    app.value.icon = props.selectedAppData.icon || 'i-lucide-app-window';
    app.value.status = props.selectedAppData.status || 'enabled';
  }
});

function closeModal() {
  app.value = {
    name: '',
    description: '',
    url: '',
    icon: 'i-lucide-app-window',
    status: 'enabled',
  };
  emit('close');
}

async function submit() {
  v$.value.$touch();
  if (v$.value.$invalid) return;

  const action = props.mode.toLowerCase();
  const payload = { ...app.value };

  if (action === 'update') {
    payload.id = props.selectedAppData.id;
  }

  isLoading.value = true;
  try {
    await store.dispatch(`iframeApps/${action}`, payload);
    useAlert(
      t(`INTEGRATION_SETTINGS.IFRAME_APPS.${props.mode}.API_SUCCESS`)
    );
    closeModal();
  } catch (err) {
    useAlert(
      t(`INTEGRATION_SETTINGS.IFRAME_APPS.${props.mode}.API_ERROR`)
    );
  } finally {
    isLoading.value = false;
  }
}
</script>

<template>
  <woot-modal :show="show" :on-close="closeModal">
    <div class="flex flex-col h-auto overflow-auto">
      <woot-modal-header :header-title="header" />
      <form class="w-full" @submit.prevent="submit">
        <woot-input
          v-model="app.name"
          :class="{ error: v$.app.name.$error }"
          class="w-full"
          :label="t('INTEGRATION_SETTINGS.IFRAME_APPS.FORM.NAME_LABEL')"
          :placeholder="
            t('INTEGRATION_SETTINGS.IFRAME_APPS.FORM.NAME_PLACEHOLDER')
          "
          :error="
            v$.app.name.$error
              ? t('INTEGRATION_SETTINGS.IFRAME_APPS.FORM.NAME_ERROR')
              : null
          "
          @input="v$.app.name.$touch"
          @blur="v$.app.name.$touch"
        />
        <woot-input
          v-model="app.description"
          class="w-full"
          :label="
            t('INTEGRATION_SETTINGS.IFRAME_APPS.FORM.DESCRIPTION_LABEL')
          "
          :placeholder="
            t('INTEGRATION_SETTINGS.IFRAME_APPS.FORM.DESCRIPTION_PLACEHOLDER')
          "
        />
        <woot-input
          v-model="app.url"
          :class="{ error: v$.app.url.$error }"
          class="w-full"
          :label="t('INTEGRATION_SETTINGS.IFRAME_APPS.FORM.URL_LABEL')"
          :placeholder="
            t('INTEGRATION_SETTINGS.IFRAME_APPS.FORM.URL_PLACEHOLDER')
          "
          :error="
            v$.app.url.$error
              ? t('INTEGRATION_SETTINGS.IFRAME_APPS.FORM.URL_ERROR')
              : null
          "
          @input="v$.app.url.$touch"
          @blur="v$.app.url.$touch"
        />
        <woot-input
          v-model="app.icon"
          class="w-full"
          :label="t('INTEGRATION_SETTINGS.IFRAME_APPS.FORM.ICON_LABEL')"
          :placeholder="
            t('INTEGRATION_SETTINGS.IFRAME_APPS.FORM.ICON_PLACEHOLDER')
          "
        />
        <div class="mb-4">
          <label class="block text-sm font-medium text-n-slate-12 mb-1">
            {{ t('INTEGRATION_SETTINGS.IFRAME_APPS.FORM.STATUS_LABEL') }}
          </label>
          <select
            v-model="app.status"
            class="w-full border border-n-weak rounded-lg px-3 py-2 text-sm bg-n-background text-n-slate-12"
          >
            <option value="enabled">
              {{ t('INTEGRATION_SETTINGS.IFRAME_APPS.FORM.STATUS_ENABLED') }}
            </option>
            <option value="disabled">
              {{ t('INTEGRATION_SETTINGS.IFRAME_APPS.FORM.STATUS_DISABLED') }}
            </option>
          </select>
        </div>
        <div class="flex flex-row justify-end w-full gap-2 px-0 py-2">
          <NextButton
            faded
            slate
            type="reset"
            :label="t('INTEGRATION_SETTINGS.IFRAME_APPS.CREATE.FORM_CANCEL')"
            @click.prevent="closeModal"
          />
          <NextButton
            type="submit"
            :label="submitButtonLabel"
            :disabled="v$.$invalid"
            :is-loading="isLoading"
          />
        </div>
      </form>
    </div>
  </woot-modal>
</template>
